import React, { useState, useMemo } from 'react';

export default function ReminderCalendar({ reminders, onDateClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get month info
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  // Calculate calendar grid
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayWeekday = firstDayOfMonth.getDay() || 7; // Sunday = 7
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Group reminders by date
  const remindersByDate = useMemo(() => {
    const grouped = {};
    reminders.forEach(reminder => {
      const date = new Date(reminder.scheduled_time);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(reminder);
    });
    return grouped;
  }, [reminders]);

  // Navigate months
  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // Generate calendar days
  const calendarDays = [];
  
  // Empty cells before first day
  for (let i = 1; i < firstDayWeekday; i++) {
    calendarDays.push({ empty: true, key: `empty-${i}` });
  }

  // Actual days
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayReminders = remindersByDate[dateKey] || [];
    const isToday = 
      new Date().getDate() === day &&
      new Date().getMonth() === month &&
      new Date().getFullYear() === year;
    
    calendarDays.push({
      day,
      dateKey,
      reminders: dayReminders,
      isToday,
      key: `day-${day}`
    });
  }

  return (
    <div className="bg-neutral-900/80 backdrop-blur-xl rounded-lg shadow-sm border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-gray-100 rounded-md"
          title="Vorheriger Monat"
        >
          ←
        </button>
        <h3 className="text-lg font-semibold">
          {monthNames[month]} {year}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-md"
          title="Nächster Monat"
        >
          →
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-600 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map(({ empty, day, dateKey, reminders, isToday, key }) => {
          if (empty) {
            return <div key={key} className="aspect-square" />;
          }

          const hasReminders = reminders && reminders.length > 0;

          return (
            <button
              key={key}
              onClick={() => hasReminders && onDateClick(dateKey, reminders)}
              className={
                'aspect-square p-1 rounded-md text-sm relative ' +
                (isToday ? 'bg-indigo-100 font-bold ' : '') +
                (hasReminders ? 'hover:bg-gray-100 cursor-pointer ' : 'cursor-default ') +
                (!hasReminders && !isToday ? 'hover:bg-gray-50 ' : '')
              }
            >
              <div className="text-center">{day}</div>
              
              {/* Reminder indicators */}
              {hasReminders && (
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                  {reminders.slice(0, 3).map((_, idx) => (
                    <div
                      key={idx}
                      className="w-1.5 h-1.5 rounded-full bg-indigo-600"
                    />
                  ))}
                  {reminders.length > 3 && (
                    <div className="text-[8px] text-indigo-600 ml-0.5">
                      +{reminders.length - 3}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-indigo-600" />
          <span>= Erinnerung vorhanden</span>
        </div>
      </div>
    </div>
  );
}
